import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { PageImage } from 'src/helpers/pageImageUtils';

interface PageImageLightboxDialogProps {
  images: PageImage[];
  open: boolean;
  activeIndex: number;
  onClose: () => void;
  onIndexChange: (index: number) => void;
}

export default function PageImageLightboxDialog({
  images,
  open,
  activeIndex,
  onClose,
  onIndexChange,
}: PageImageLightboxDialogProps) {
  const { t } = useTranslation();
  const activeImage = images[activeIndex];
  const hasMultiple = images.length > 1;

  const goPrevious = useCallback(() => {
    onIndexChange((activeIndex - 1 + images.length) % images.length);
  }, [activeIndex, images.length, onIndexChange]);

  const goNext = useCallback(() => {
    onIndexChange((activeIndex + 1) % images.length);
  }, [activeIndex, images.length, onIndexChange]);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'ArrowLeft' && hasMultiple) {
        goPrevious();
      } else if (event.key === 'ArrowRight' && hasMultiple) {
        goNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, hasMultiple, onClose, goPrevious, goNext]);

  if (!activeImage) return null;

  const caption = activeImage.alt.trim();
  const titleId = 'page-image-lightbox-title';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      aria-labelledby={titleId}
      slotProps={{
        backdrop: {
          sx: { bgcolor: 'rgba(0, 0, 0, 0.92)' },
        },
        paper: {
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
            maxWidth: '100vw',
            width: '100%',
            m: 0,
          },
        },
      }}
    >
      <DialogTitle
        id={titleId}
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          p: 0,
          m: -1,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {t('imageLightbox.title')}
      </DialogTitle>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: { xs: '70vh', sm: '80vh' },
          px: { xs: 1, sm: 6 },
          py: 2,
        }}
      >
        <IconButton
          onClick={onClose}
          aria-label={t('base.close')}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'common.white',
            bgcolor: 'rgba(0, 0, 0, 0.35)',
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.55)' },
          }}
        >
          <CloseIcon />
        </IconButton>

        {hasMultiple ? (
          <IconButton
            onClick={goPrevious}
            aria-label={t('imageLightbox.previous')}
            sx={{
              position: 'absolute',
              left: { xs: 4, sm: 16 },
              color: 'common.white',
              bgcolor: 'rgba(0, 0, 0, 0.35)',
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.55)' },
            }}
          >
            <ChevronLeftIcon fontSize="large" />
          </IconButton>
        ) : null}

        <Box
          component="figure"
          sx={{
            m: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            maxWidth: '100%',
          }}
        >
          <Box
            component="img"
            src={activeImage.src}
            alt={caption ? '' : activeImage.alt}
            sx={{
              maxWidth: '100%',
              maxHeight: { xs: '70vh', sm: '85vh' },
              objectFit: 'contain',
              display: 'block',
              borderRadius: 1,
            }}
          />

          {caption ? (
            <Typography
              component="figcaption"
              variant="body2"
              sx={{
                mt: 1.5,
                px: 2,
                color: 'common.white',
                textAlign: 'center',
                maxWidth: '100%',
              }}
            >
              {caption}
            </Typography>
          ) : null}
        </Box>

        {hasMultiple ? (
          <IconButton
            onClick={goNext}
            aria-label={t('imageLightbox.next')}
            sx={{
              position: 'absolute',
              right: { xs: 4, sm: 16 },
              color: 'common.white',
              bgcolor: 'rgba(0, 0, 0, 0.35)',
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.55)' },
            }}
          >
            <ChevronRightIcon fontSize="large" />
          </IconButton>
        ) : null}

        {hasMultiple ? (
          <Typography
            variant="body2"
            sx={{
              position: 'absolute',
              bottom: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'common.white',
              bgcolor: 'rgba(0, 0, 0, 0.45)',
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
            }}
          >
            {t('imageLightbox.counter', {
              current: activeIndex + 1,
              total: images.length,
            })}
          </Typography>
        ) : null}
      </Box>
    </Dialog>
  );
}
